import type {
  AuthorizationResponse,
  ExecuteToolResponse,
} from "@arcadeai/arcadejs/resources/index.mjs";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";
import { waitForAuthCompletion } from "@/app/playground/api/wait-for-auth-completion";
import { toolsKeys } from "@/app/tools/api/query-keys";
import type { Tool } from "@/app/tools/types";
import { getEngineApi } from "@/lib/api";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import type { PlaygroundResponse } from "./use-store";
import { usePlaygroundStore } from "./use-store";

function toInputValues(
  values: Record<string, unknown>
): Record<string, string | string[]> {
  const result: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    if (Array.isArray(value)) {
      const filtered = value.map(String).filter((s) => s !== "");
      if (filtered.length > 0) {
        result[key] = filtered;
      }
    } else {
      result[key] = String(value);
    }
  }
  return result;
}

type UseArcadeClientOptions = {
  userId?: string;
};

/**
 * Hook that provides tool execution and authorization for the playground.
 */
export function useArcadeClient({ userId }: UseArcadeClientOptions = {}) {
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const queryClient = useQueryClient();

  const selectedTool = usePlaygroundStore((state) => state.selectedTool);
  const setLoading = usePlaygroundStore((state) => state.setLoading);
  const setResponse = usePlaygroundStore((state) => state.setResponse);
  const setAuthState = usePlaygroundStore((state) => state.setAuthState);

  const invalidateToolsQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: toolsKeys.all({
        scope: { organizationId, projectId },
      }),
    });
  }, [queryClient, organizationId, projectId]);

  const authorizeTool = useCallback(
    async (tool: Tool): Promise<boolean> => {
      if (!tool.requirements?.authorization) {
        return true;
      }

      const client = getEngineApi({ organizationId, projectId });

      try {
        const { data: auth } = await client.post<AuthorizationResponse>(
          "/tools/authorize",
          { tool_name: tool.fully_qualified_name }
        );

        if (auth?.status === "completed") {
          setAuthState({ status: "completed" });
          invalidateToolsQuery();
          return true;
        }

        const authUrl = auth?.url;
        setAuthState({ status: "pending", url: authUrl });
        if (authUrl) {
          window.open(authUrl, "_blank", "noopener,noreferrer");
        }

        if (!auth.id) {
          setAuthState({
            status: "failed",
            error: "Authorization ID is missing",
          });
          return false;
        }

        const completion = await waitForAuthCompletion(
          auth.id,
          organizationId,
          projectId
        );

        if (completion?.status !== "completed") {
          setAuthState({
            status: "failed",
            error: "Authorization was not completed",
          });
          toast.error("Tool authorization failed");
          return false;
        }

        setAuthState({ status: "completed" });
        invalidateToolsQuery();
        return true;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        setAuthState({ status: "failed", error: message });
        toast.error("Failed to authorize tool");
        return false;
      }
    },
    [projectId, organizationId, setAuthState, invalidateToolsQuery]
  );

  const executeTool = useCallback(
    async (values: Record<string, unknown>) => {
      if (!selectedTool) {
        return;
      }

      setLoading(true);

      try {
        const authSuccess = await authorizeTool(selectedTool);
        if (!authSuccess && selectedTool.requirements?.authorization) {
          return;
        }

        // Reuse a single client for the execute call — avoids creating
        // a second instance (authorizeTool already created one above).
        const client = getEngineApi({ organizationId, projectId });
        const { data: response } = await client.post<ExecuteToolResponse>(
          "/tools/execute",
          {
            tool_name: selectedTool.fully_qualified_name,
            ...(userId && { user_id: userId }),
            input: toInputValues(values),
          }
        );

        setResponse(response);
      } catch (error) {
        toast.error("Failed to execute tool");
        const errorResponse: PlaygroundResponse = {
          status: "error",
          message: error instanceof Error ? error.message : "Unknown error",
          name: error instanceof Error ? error.name : "Error",
        };
        setResponse(errorResponse);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedTool,
      projectId,
      organizationId,
      userId,
      setLoading,
      setResponse,
      authorizeTool,
    ]
  );

  const startAuthorization = useCallback(async () => {
    if (!selectedTool) {
      return;
    }
    await authorizeTool(selectedTool);
  }, [selectedTool, authorizeTool]);

  return { executeTool, startAuthorization };
}
